import { useEffect, useState } from "react";
import { Eye, EyeOff, Copy, RotateCw, Plug } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export const StoreConnection = () => {
  const [s, setS] = useState(null);
  const [url, setUrl] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/store/settings").then((r) => { setS(r.data); setUrl(r.data.webhook_url); }).catch((e) => toast.error(errMsg(e)));
  }, []);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const save = () => run("save", async () => { const r = await api.put("/store/settings", { webhook_url: url, notify_buyers: s.notify_buyers }); setS(r.data); }, "Store endpoint saved");
  const [own, setOwn] = useState("");
  const useOwn = () => run("own", async () => { const r = await api.put("/store/settings/secret", { secret: own.trim() }); setS(r.data); setOwn(""); window.dispatchEvent(new CustomEvent("store-secret-changed", { detail: r.data.secret })); }, "Secret saved — products and download links are now signed with your key");
  const rotate = () => run("rotate", async () => { const r = await api.post("/store/settings/rotate-secret"); setS(r.data); window.dispatchEvent(new CustomEvent("store-secret-changed", { detail: r.data.secret })); }, "New secret generated — update EXPAT_STUDIO_SECRET on your site");
  const ping = () => run("ping", async () => {
    const r = await api.post("/store/ping");
    setS({ ...s, last_ping: r.data });
    r.data.ok ? toast.success("theexpatstudio.com responded OK") : toast.error(`Store responded ${r.data.status_code ?? "—"}: ${r.data.response}`);
  });

  if (!s) return <p className="eyebrow">Loading connection…</p>;
  const lp = s.last_ping;

  return (
    <div className="bg-[#3B1B48] text-[#FAF7F2] p-8 space-y-6" data-testid="store-connection">
      <div className="flex items-center gap-3">
        <Plug className="w-5 h-5 text-[#C59B27]" strokeWidth={1.4} />
        <p className="eyebrow">Connection to theexpatstudio.com</p>
        <span data-testid="store-connection-status" className={`ml-auto text-[11px] font-mono-accent uppercase tracking-wider ${s.connected ? "text-[#C59B27]" : "text-[#EFE6F3]/60"}`}>{s.connected ? "Configured" : "Not set up"}</span>
      </div>
      <div className="space-y-2">
        <Label htmlFor="webhook" className="text-[#EFE6F3]">Product import endpoint</Label>
        <div className="flex gap-2">
          <Input id="webhook" data-testid="store-webhook-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://theexpatstudio.com/api/expat-guides/import" className="bg-white text-[#1C181D] rounded-none h-11" />
          <Button data-testid="store-webhook-save-button" onClick={save} disabled={busy === "save"} className="rounded-none h-11 bg-[#C59B27] hover:bg-[#b08a20] text-[#1C181D]">Save</Button>
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-[#EFE6F3]">Signing secret</Label>
        <div className="flex items-center gap-3 bg-[#1C181D]/40 px-4 h-11">
          <code data-testid="store-secret" className="font-mono-accent text-xs flex-1 truncate">{show ? s.secret : "•".repeat(32)}</code>
          <button data-testid="store-secret-toggle" onClick={() => setShow(!show)} className="text-[#C59B27] hover:text-white">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          <button data-testid="store-secret-copy" onClick={() => navigator.clipboard.writeText(s.secret).then(() => toast.success("Secret copied"))} className="text-[#C59B27] hover:text-white"><Copy className="w-4 h-4" /></button>
          <button data-testid="store-secret-rotate" onClick={rotate} disabled={busy === "rotate"} className="text-[#C59B27] hover:text-white"><RotateCw className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2 pt-1">
          <Input data-testid="store-own-secret-input" type="password" value={own} onChange={(e) => setOwn(e.target.value)} placeholder="Or paste your own secret (32+ letters, numbers, - or _)" className="bg-white text-[#1C181D] rounded-none h-10 text-xs" />
          <Button data-testid="store-own-secret-save" onClick={useOwn} disabled={own.trim().length < 32 || busy === "own"} className="rounded-none h-10 bg-[#C59B27] hover:bg-[#b08a20] text-[#1C181D]">Use this secret</Button>
        </div>
        <p className="text-xs text-[#EFE6F3]/70">Paste this into EXPAT_STUDIO_SECRET on your site. Every request is signed with it so nobody else can add products or create download links.</p>
      </div>
      <div className="flex items-center gap-4">
        <Button data-testid="store-ping-button" onClick={ping} disabled={!s.connected || busy === "ping"} variant="outline" className="rounded-none border-[#C59B27] text-[#C59B27] bg-transparent hover:bg-[#C59B27] hover:text-[#1C181D]">
          {busy === "ping" ? "Testing…" : "Test connection"}
        </Button>
        {lp && <span data-testid="store-ping-result" className={`text-xs ${lp.ok ? "text-[#C59B27]" : "text-red-300"}`}>{lp.ok ? "Connected" : `Failed (${lp.status_code ?? "no response"})`} · {new Date(lp.at).toLocaleString("en-AU")}</span>}
      </div>
      <label className="flex items-center gap-3 text-sm text-[#EFE6F3]">
        <Switch data-testid="notify-buyers-toggle" checked={s.notify_buyers} onCheckedChange={(v) => run("notify", async () => { const r = await api.put("/store/settings", { webhook_url: s.webhook_url, notify_buyers: v }); setS(r.data); }, v ? "Past buyers will be emailed new editions" : "Automatic buyer emails off")} />
        Automatically email past buyers fresh download links when a new edition is ready
      </label>
    </div>
  );
};
